/**
 * Canonical Merchant Context Panel (Step 4).
 * Reusable on Leads, Opportunities, Proposals and Applications — tells a rep immediately
 * whether ISOCNTRL already knows this merchant, and what history exists.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Building2, ChevronDown, ChevronUp, CreditCard, Clock } from 'lucide-react';
import { LinkMerchantDialog } from './LinkMerchantDialog';

const STATUS_STYLES = {
  active: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  approved: 'bg-sky-500/15 text-sky-600 border-sky-500/30',
  pending: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  submitted: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  suspended: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
  closed: 'bg-muted text-muted-foreground border-border',
};

const COUNT_LABELS = [
  ['leads', 'Leads'],
  ['opportunities', 'Opportunities'],
  ['proposals', 'Proposals'],
  ['applications', 'Applications'],
  ['locations', 'Locations'],
  ['processing_accounts', 'Processing accounts'],
];

export const MerchantContextPanel = ({ businessId: initialId, recordType, recordId, onChange }) => {
  const [businessId, setBusinessId] = useState(initialId);
  const [linking, setLinking] = useState(false);
  const [context, setContext] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setBusinessId(initialId); }, [initialId]);
  const canLink = Boolean(recordType && recordId);
  const linkButton = canLink && (
    <Button variant="outline" size="sm" onClick={() => setLinking(true)} data-testid="link-merchant-btn">{businessId ? 'Change merchant' : 'Link to merchant'}</Button>
  );
  const dialog = linking && (
    <LinkMerchantDialog recordType={recordType} recordId={recordId} linked={Boolean(businessId)} onClose={() => setLinking(false)}
      onLinked={(id) => { setLinking(false); setContext(null); setTimeline(null); setLoading(true); setBusinessId(id); onChange?.(id); }} />
  );

  const load = useCallback(async () => {
    if (!businessId) {
      setContext(null);
      setLoading(false);
      return;
    }
    try {
      setContext(await api.getBusinessContext(businessId));
    } catch (e) {
      setContext(null);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && !timeline) {
      try {
        const data = await api.getBusinessTimeline(businessId, 200);
        setTimeline(data.events || []);
      } catch (e) {
        setTimeline([]);
      }
    }
  };

  if (loading) return null;
  if (!context) {
    return (
      <div
        className="rounded-xl border border-dashed border-border bg-card/20 p-5"
        data-testid="merchant-context-panel"
      >
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Canonical business</p>
        </div>
        <p className="text-sm text-muted-foreground mt-2" data-testid="merchant-context-empty">
          Not linked to a merchant yet. Link it so this record's history, MIDs and residuals line up with the business.
        </p>
        {linkButton && <div className="mt-3">{linkButton}</div>}
        {dialog}
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-border bg-card/40 p-5 space-y-4"
      data-testid="merchant-context-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Canonical business</p>
          </div>
          <p className="text-base md:text-lg font-medium" data-testid="context-legal-name">
            {context.legal_name || '—'}
          </p>
          {context.dba_names?.length > 0 && (
            <p className="text-sm text-muted-foreground">DBA: {context.dba_names.join(', ')}</p>
          )}
        </div>
        {context.is_existing_merchant && (
          <Badge
            variant="outline"
            className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30"
            data-testid="context-existing-merchant-badge"
          >
            We already board this merchant
          </Badge>
        )}
        {linkButton}
      </div>
      {dialog}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="context-counts">
        {COUNT_LABELS.map(([key, label]) => (
          <div key={key} className="rounded-lg border border-border/60 px-3 py-2">
            <p className="text-xl font-semibold" data-testid={`context-count-${key}`}>
              {context.counts?.[key] ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      {context.processing_accounts?.length > 0 && (
        <div className="space-y-2" data-testid="context-mid-list">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Current MIDs</p>
          </div>
          {context.processing_accounts.map((pa) => (
            <div
              key={pa.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2"
              data-testid={`context-mid-${pa.id}`}
            >
              <span className="text-sm font-mono">{pa.mid || 'pending'}</span>
              <span className="text-sm text-muted-foreground">{pa.processor || '—'}</span>
              <Badge variant="outline" className={STATUS_STYLES[pa.status] || STATUS_STYLES.pending}>
                {pa.status}
              </Badge>
            </div>
          ))}
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={toggleHistory}
        data-testid="merchant-timeline-toggle"
      >
        <Clock className="h-4 w-4 mr-2" />
        {showHistory ? 'Hide merchant history' : 'Show merchant history'}
        {showHistory ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
      </Button>

      {showHistory && (
        <ol className="relative border-l border-border pl-5 space-y-4" data-testid="merchant-timeline">
          {timeline === null && <li className="text-sm text-muted-foreground">Loading history…</li>}
          {timeline?.length === 0 && (
            <li className="text-sm text-muted-foreground">No history recorded yet.</li>
          )}
          {timeline?.map((event, idx) => (
            <li key={`${event.kind}-${idx}`} className="space-y-0.5" data-testid={`timeline-event-${idx}`}>
              <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
              <p className="text-xs text-muted-foreground">
                {new Date(event.at).toLocaleDateString()} ·{' '}
                {new Date(event.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-sm font-medium">{event.title}</p>
              {event.detail && <p className="text-xs text-muted-foreground">{event.detail}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default MerchantContextPanel;
